// Spawn data-driven enemies into EntityStore.

import type { World } from './world';
import { addEntity } from './entity';
import { rand } from './rng';
import { rooms } from './state';
import { getSpawnableEnemies } from '../data/loader';
import type { EnemyDef } from '../data/schemas';
import { getBehavior } from './behaviors/registry';
import { TILE } from './constants';

let contentSpawnEnabled = false;

export function setContentSpawnEnabled(enabled: boolean): void {
  contentSpawnEnabled = enabled;
}

export function isContentSpawnEnabled(): boolean {
  return contentSpawnEnabled;
}

function roomMatches(def: EnemyDef, roomId: number): boolean {
  const spawn = def.spawn;
  if (!spawn) return false;
  const room = rooms[roomId];
  if (spawn.roomTypes && !spawn.roomTypes.includes(room.room_type)) return false;
  if (spawn.minDist !== undefined && room.s_dist < spawn.minDist) return false;
  if (spawn.chance !== undefined && rand() % 1000 > spawn.chance * 1000) return false;
  return true;
}

function pickWeighted(candidates: EnemyDef[]): EnemyDef | null {
  let total = 0;
  for (const c of candidates) total += c.spawn?.weight ?? 0;
  if (total <= 0) return null;
  let roll = rand() % total;
  for (const c of candidates) {
    roll -= c.spawn?.weight ?? 0;
    if (roll < 0) return c;
  }
  return candidates[candidates.length - 1] ?? null;
}

export function spawnEntityFromDef(
  world: World,
  def: EnemyDef,
  x: number,
  y: number,
  roomId: number
): void {
  const entity = addEntity(world.entities, {
    kind: 'enemy',
    pos: { x, y },
    vel: { x: 0, y: 0 },
    hp: def.stats.hp,
    maxHp: def.stats.hp,
    behaviorId: def.behavior,
    renderId: def.render.sprite,
    roomId,
    flags: 0,
    data: {
      damage: def.stats.damage,
      speed: def.stats.speed,
      gems: def.stats.gems,
      str: def.stats.hp,
      anim: def.render.anim,
      frames: def.render.frames,
      fps: def.render.fps,
      defId: def.id,
    },
  });
  getBehavior(def.behavior)?.onSpawn?.(world, entity);
}

export function spawnContentEnemiesForRoom(world: World, roomId: number): void {
  if (!contentSpawnEnabled) return;
  const candidates = getSpawnableEnemies().filter((d) => roomMatches(d, roomId));
  const picked = pickWeighted(candidates);
  if (!picked) return;

  const room = rooms[roomId];
  const rx = room.x * TILE + TILE + (rand() % Math.max(1, room.w * TILE - TILE * 2));
  const ry = room.y * TILE + TILE + (rand() % Math.max(1, room.h * TILE - TILE * 2));
  spawnEntityFromDef(world, picked, rx, ry, roomId);
}
